import html2canvas from 'html2canvas';

// Max canvas dimension before we downscale (keeps compression fast and file small)
const MAX_CANVAS_DIMENSION = 2000;
// Target file size (1MB) so uploads work with typical proxy limits (e.g. nginx default 1m)
const TARGET_FILE_SIZE_BYTES = 1 * 1024 * 1024;

/**
 * Downscale canvas so the longest side is at most maxDim. Returns a new canvas or the original if already small enough.
 */
function downscaleCanvasIfNeeded(sourceCanvas, maxDim) {
  const w = sourceCanvas.width;
  const h = sourceCanvas.height;
  if (w <= maxDim && h <= maxDim) return sourceCanvas;
  const scale = maxDim / Math.max(w, h);
  const nw = Math.round(w * scale);
  const nh = Math.round(h * scale);
  const out = document.createElement('canvas');
  out.width = nw;
  out.height = nh;
  const ctx = out.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0, w, h, 0, 0, nw, nh);
  return out;
}

/**
 * Capture viewport-only screenshot (one screen) of the current page, excluding specified element.
 * Includes portal-rendered dialogs (e.g. Add Assembly). Empty areas use white background.
 * @param {string} excludeElementId - ID of element to exclude from screenshot (e.g. feedback dialog)
 * @returns {Promise<File>} File object ready for upload (JPEG or PNG, ≤1MB target)
 */
export async function captureScreenshot(excludeElementId) {
  try {
    // Hide only the feedback dialog (so it's not in the shot), not other modals (e.g. Add/Edit Assembly or Component)
    const originalStyles = [];
    const excludeEl = excludeElementId ? document.getElementById(excludeElementId) : null;
    const modalToHide = excludeEl?.closest('.MuiModal-root') || excludeEl?.closest('.MuiDialog-root');
    const elementsToHide = modalToHide ? [modalToHide] : [];
    if (modalToHide?.previousElementSibling?.classList?.contains('MuiBackdrop-root')) {
      elementsToHide.unshift(modalToHide.previousElementSibling);
    }
    elementsToHide.forEach((el) => {
      originalStyles.push({
        element: el,
        display: el.style.display,
        visibility: el.style.visibility
      });
      el.style.display = 'none';
      el.style.visibility = 'hidden';
    });

    await new Promise(resolve => setTimeout(resolve, 100));

    // Viewport-only: capture body so portal dialogs are included; clone shows only the visible viewport
    const scrollY = window.scrollY ?? document.documentElement.scrollTop ?? 0;

    const canvas = await html2canvas(document.body, {
      useCORS: true,
      scale: 0.8,
      logging: false,
      allowTaint: true,
      removeContainer: true,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      foreignObjectRendering: false,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        const scripts = clonedDoc.querySelectorAll('script');
        scripts.forEach(script => script.remove());
        const body = clonedDoc.body;
        const html = clonedDoc.documentElement;
        // Constrain clone to viewport size and show current scroll position
        body.style.width = `${window.innerWidth}px`;
        body.style.height = `${window.innerHeight}px`;
        body.style.overflow = 'hidden';
        body.style.margin = '0';
        body.style.padding = '0';
        body.style.backgroundColor = '#ffffff';
        body.style.transform = `translateY(-${scrollY}px)`;
        if (html) {
          html.style.margin = '0';
          html.style.padding = '0';
          html.style.backgroundColor = '#ffffff';
        }
      }
    });

    // Restore original styles
    originalStyles.forEach(({ element, display, visibility }) => {
      element.style.display = display;
      element.style.visibility = visibility;
    });

    // Downscale canvas if very large so compression is fast and output stays small
    const canvasToUse = downscaleCanvasIfNeeded(canvas, MAX_CANVAS_DIMENSION);

    return new Promise((resolve, reject) => {
      canvasToUse.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create screenshot blob'));
            return;
          }

          if (blob.size <= TARGET_FILE_SIZE_BYTES) {
            resolve(new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' }));
            return;
          }

          let quality = 0.85;
          const compressImage = (currentQuality) => {
            canvasToUse.toBlob(
              (compressedBlob) => {
                if (!compressedBlob) {
                  reject(new Error('Failed to compress screenshot'));
                  return;
                }
                if (compressedBlob.size <= TARGET_FILE_SIZE_BYTES || currentQuality <= 0.15) {
                  resolve(new File([compressedBlob], `screenshot-${Date.now()}.jpg`, {
                    type: 'image/jpeg'
                  }));
                } else {
                  compressImage(Math.max(0.15, currentQuality - 0.1));
                }
              },
              'image/jpeg',
              currentQuality
            );
          };
          compressImage(quality);
        },
        'image/png',
        0.9
      );
    });
  } catch (error) {
    console.error('Error capturing screenshot:', error);
    throw error;
  }
}

